import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  AccountBalance as BankIcon,
  Receipt as VATIcon,
  CheckCircle as CheckIcon,
  Flag as USFlagIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  TrendingUp as GrowthIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useOnboarding } from '../contexts/OnboardingContext';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    onboardingData,
    updateOnboardingData,
    completeOnboarding,
    completionPercentage,
  } = useOnboarding();

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = () => {
    completeOnboarding();
    navigate('/dashboard');
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    updateOnboardingData({ [field]: value });
  };

  const steps = [
    {
      label: 'Welcome to Executive Finance US',
      content: (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            �🇸 Welcome to the Future of US Financial Management
          </Typography>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              📋 Complete Your Profile
            </Typography>
            <Typography variant="body2">
              Filling out all fields gives us a <strong>clear picture of your company</strong> and helps our AI assistant
              provide more relevant, personalized answers to your questions. The more we know, the better we can serve you!
            </Typography>
          </Alert>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <SecurityIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>US Compliant</Typography>
                <Typography variant="body2" color="text.secondary">
                  IRS E-File ready, Secretary of State integration, full sales tax compliance
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <SpeedIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>Modern & Fast</Typography>
                <Typography variant="body2" color="text.secondary">
                  Superior UX that beats Sage and Xero, mobile-first design
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <GrowthIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>AI-Powered</Typography>
                <Typography variant="body2" color="text.secondary">
                  Cross-domain intelligence and automated insights
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              🚀 Why Choose Executive Finance US?
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Save 5+ hours per week on financial admin" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Reduce accounting costs by up to 60%" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText primary="Never miss a Sales Tax return or Secretary of State deadline" />
              </ListItem>
            </List>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Company Information',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Tell us about your business
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              💡 Why this matters
            </Typography>
            <Typography variant="body2">
              Your AI agents will provide more relevant insights when they understand your business.
              The more context you share, the smarter your recommendations become.
            </Typography>
          </Alert>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={onboardingData.companyName}
                onChange={(e) => handleFieldChange('companyName', e.target.value)}
                placeholder="e.g., TechCorp Solutions Inc"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={onboardingData.businessType}
                  label="Business Type"
                  onChange={(e) => handleFieldChange('businessType', e.target.value)}
                >
                  <MenuItem value="sole_trader">Sole Trader</MenuItem>
                  <MenuItem value="partnership">Partnership</MenuItem>
                  <MenuItem value="limited_company">Limited Company</MenuItem>
                  <MenuItem value="llp">Limited Liability Partnership (LLP)</MenuItem>
                  <MenuItem value="charity">Charity</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={onboardingData.industry}
                  label="Industry"
                  onChange={(e) => handleFieldChange('industry', e.target.value)}
                >
                  <MenuItem value="technology">Technology</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="professional_services">Professional Services</MenuItem>
                  <MenuItem value="construction">Construction</MenuItem>
                  <MenuItem value="hospitality">Hospitality</MenuItem>
                  <MenuItem value="manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="healthcare">Healthcare</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={onboardingData.postcode}
                onChange={(e) => handleFieldChange('postcode', e.target.value.toUpperCase())}
                placeholder="e.g., 10001"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Number of Employees</InputLabel>
                <Select
                  value={onboardingData.employeeCount}
                  label="Number of Employees"
                  onChange={(e) => handleFieldChange('employeeCount', e.target.value)}
                >
                  <MenuItem value="1">Just me</MenuItem>
                  <MenuItem value="2-10">2-10</MenuItem>
                  <MenuItem value="11-50">11-50</MenuItem>
                  <MenuItem value="51-200">51-200</MenuItem>
                  <MenuItem value="200+">200+</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secretary of State Number (Optional)"
                value={onboardingData.companyNumber}
                onChange={(e) => handleFieldChange('companyNumber', e.target.value)}
                placeholder="e.g., 12345678"
                helperText="For Limited Companies only"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax ID / EIN (Optional)"
                value={onboardingData.vatNumber}
                onChange={(e) => handleFieldChange('vatNumber', e.target.value.toUpperCase())}
                placeholder="e.g., 12-3456789"
                helperText="If sales tax registered"
              />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mt: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="subtitle2" gutterBottom>
              🔒 Your Data is Secure
            </Typography>
            <Typography variant="body2">
              All information is encrypted and stored securely in US data centers.
              We're CCPA compliant and never share your data with third parties.
            </Typography>
          </Paper>
        </Box>
      )
    },
    {
      label: 'Financial Details',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VATIcon color="primary" />
            Financial Information
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Optional but helpful:</strong> This information allows us to provide more accurate financial insights
            and tailor recommendations to your business size.
          </Alert>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
            💡 <strong>Pro tip:</strong> For deeper insights, add your business plan. Don't have one? Use the Business Planner
            in your Force CRM app — it's on the left navigation pane.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Annual Revenue (Optional)</InputLabel>
                <Select
                  value={onboardingData.annualRevenue}
                  label="Annual Revenue (Optional)"
                  onChange={(e) => handleFieldChange('annualRevenue', e.target.value)}
                >
                  <MenuItem value="under_85k">Under $85,000</MenuItem>
                  <MenuItem value="85k-250k">$85,000 - $250,000</MenuItem>
                  <MenuItem value="250k-1m">$250,000 - $1 million</MenuItem>
                  <MenuItem value="1m-5m">$1 million - $5 million</MenuItem>
                  <MenuItem value="5m+">$5 million+</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Financial Year End (Optional)</InputLabel>
                <Select
                  value={onboardingData.financialYearEnd}
                  label="Financial Year End (Optional)"
                  onChange={(e) => handleFieldChange('financialYearEnd', e.target.value)}
                >
                  <MenuItem value="march">31st March</MenuItem>
                  <MenuItem value="april">5th April (Tax Year)</MenuItem>
                  <MenuItem value="december">31st December</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>VAT Scheme (Optional)</InputLabel>
                <Select
                  value={onboardingData.vatScheme}
                  label="VAT Scheme (Optional)"
                  onChange={(e) => handleFieldChange('vatScheme', e.target.value)}
                >
                  <MenuItem value="not_registered">Not VAT Registered</MenuItem>
                  <MenuItem value="standard">Standard VAT</MenuItem>
                  <MenuItem value="flat_rate">Flat Rate Scheme</MenuItem>
                  <MenuItem value="cash">Cash Accounting</MenuItem>
                  <MenuItem value="annual">Annual Accounting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Card sx={{ p: 2, mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              US Sales Tax Rates Reference
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Standard Rate: 20%"
                  secondary="Most goods and services"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Reduced Rate: 5%"
                  secondary="Energy, children's items"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Zero Rate: 0%"
                  secondary="Books, food, children's clothes"
                />
              </ListItem>
            </List>
            <Chip label="IRS E-File Ready" color="success" size="small" />
          </Card>
        </Box>
      )
    },
    {
      label: 'Banking & Integration',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BankIcon color="primary" />
            Connect Your Bank Account
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              🏦 Open Banking Integration
            </Typography>
            <Typography variant="body2">
              Securely connect your US bank account for automatic transaction import.
              We support all major US banks including Chase, Bank of America, Wells Fargo, Citi, and more.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>Supported Banks</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {['Chase', 'Bank of America', 'Wells Fargo', 'Citi', 'U.S. Bank', 'PNC'].map((bank) => (
                    <Chip key={bank} label={bank} size="small" variant="outlined" />
                  ))}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>Benefits</Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Automatic transaction import" secondary="Save hours of manual entry" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Real-time bank reconciliation" secondary="Always up-to-date" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Secure & encrypted" secondary="Bank-level security" />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button variant="outlined" size="large" sx={{ mr: 2 }}>
              Connect Bank Later
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleFieldChange('bankConnected', true)}
            >
              Connect Bank Now
            </Button>
          </Box>
        </Box>
      )
    },
    {
      label: 'You\'re All Set!',
      content: (
        <Box textAlign="center">
          <CheckIcon color="success" sx={{ fontSize: 80, mb: 2 }} />

          <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
            Welcome to Executive Finance UK! 🎉
          </Typography>

          <Typography variant="h6" color="text.secondary" paragraph>
            Your financial management system is ready to use
          </Typography>

          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>
              ✅ Profile Completion: {completionPercentage}%
            </Typography>
            <Typography variant="body2">
              {completionPercentage === 100
                ? 'Excellent! You have provided all the information we need to serve you best.'
                : 'You can always add more details later from your settings page.'}
            </Typography>
          </Alert>

          <Grid container spacing={2} sx={{ mt: 3, mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">Next Steps</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Explore your dashboard" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Add your first customers" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Create your first invoice" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">Need Help?</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="📖 User Guide" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="❓ FAQs" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="💬 AI Chat Assistant" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="primary">Resources</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="🎥 Video Tutorials" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="📊 Sample Reports" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="🔗 API Documentation" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Progress Bar at Top */}
      <Paper sx={{ p: 2, mb: 4, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Profile Completion
          </Typography>
          <Typography variant="subtitle2" color="primary" fontWeight="bold">
            {completionPercentage}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionPercentage}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              backgroundColor: completionPercentage === 100 ? 'success.main' : 'primary.main',
            }
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The more you fill in, the better our AI can understand and assist your business
        </Typography>
      </Paper>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <USFlagIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
          Executive Finance US
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Get started in just 5 minutes
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>
              <Typography variant="h6">{step.label}</Typography>
            </StepLabel>
            <StepContent>
              {step.content}
              <Box sx={{ mb: 2, mt: 3 }}>
                {index === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleComplete}
                    size="large"
                    sx={{ mr: 1 }}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mr: 1 }}
                    disabled={index === 1 && !onboardingData.companyName}
                  >
                    Continue
                  </Button>
                )}
                <Button
                  disabled={index === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default OnboardingPage;
